import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../../AppContext";
import { ICalendarEventModel } from "../models/calendarEventModel";
import { IEventDetailsContentProps } from "./useEventDetailsContent";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import {
  callPhoneNumber,
  constructMyReservationsReservationHref,
  generateMapsLink,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { getEventDetailsContentWarningMessage } from "../utils/portalCalendarUtility";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { isReservationActive } from "../../../../common/infrastructure/utilities/reservationUtility";
import { savePendingReservationIntent } from "../../../../common/infrastructure/utilities/pendingReservationUtility";
import { getEventCalendarTimeLabel } from "../../../../common/infrastructure/utilities/durationUtility";

interface IUseEventDetailsProps {
  currentEvent?: ICalendarEventModel;
  responsibleForUsersIds?: number[];
  onEventSignUp: (eventId?: number) => void;
}

export const useEventDetails = ({
  currentEvent,
  responsibleForUsersIds,
  onEventSignUp,
}: IUseEventDetailsProps) => {
  const { selectedStudio, currentUser } = useAppContext();
  const navigate = useNavigate();

  const notEveryoneHasReservation =
    responsibleForUsersIds?.some(
      (userId) =>
        !currentEvent?.userReservations?.some(
          (r) =>
            r.userId === userId && isReservationActive(r.isCancelled, r.status),
        ),
    ) ?? true;

  const atLeastOneReservation =
    responsibleForUsersIds?.some((userId) =>
      currentEvent?.userReservations?.some(
        (r) =>
          r.userId === userId && isReservationActive(r.isCancelled, r.status),
      ),
    ) ?? false;

  const noReservations = currentEvent?.userReservations.length === 0;

  const makeReservationButtonLabel = currentEvent?.hasAvailableSpaces
    ? noReservations
      ? "Направи резервация"
      : "Направи нова резервация"
    : noReservations
      ? "Направи резервация в изчакващи"
      : "Направи нова резервация в изчакващи";

  const isGuest = !currentUser || currentUser?.permissions.length === 0;
  const isCancelled = !!currentEvent?.isCancelled;

  const warningMessage = getEventDetailsContentWarningMessage({
    isPassed: currentEvent?.isPassed,
    eventType: currentEvent?.userReservations?.[0]?.type,
    hasEveryoneReserved: isGuest ? false : !notEveryoneHasReservation,
    areReservationsClosed: !currentEvent?.isReservationOpen,
    canCancelReservations: undefined,
    status: currentEvent?.userReservations?.[0]?.status,
  });
  const onlineReservationsAllowed =
    currentEvent?.allowOnlineReservations !== false;
  const canMakeReservation =
    !isCancelled &&
    onlineReservationsAllowed &&
    (isGuest
      ? !!currentEvent?.isReservationOpen
      : !!notEveryoneHasReservation &&
        !!currentEvent?.isReservationOpen &&
        !currentUser.isUnreliable);
  const hasReservations = !!atLeastOneReservation;

  const onManageReservationsClick = () => {
    const firstReservationId =
      !!currentEvent?.userReservations &&
      currentEvent.userReservations.length === 1
        ? currentEvent?.userReservations[0].reservationId
        : 0;

    if (firstReservationId > 0) {
      navigate(constructMyReservationsReservationHref(firstReservationId));
    } else {
      navigate(routePaths.portalMyReservations);
    }
  };

  const handleEventSignUp = () => {
    // Full event (no available spaces) → confirm joining the waitlist.
    // Prepaid events with available spaces are handled by the parent hook,
    // which opens the payment-method dialog after family-member selection.
    if (!currentEvent?.hasAvailableSpaces) {
      showCustomConfirmDialog(
        <>
          <div>Искате ли да се запишете към списъка на чакащите?</div>
          <br />
          <div>
            Ако се освободи място, ще получите имейл с линк за потвърждение.
          </div>
          <div>Първият, който кликне и потвърди, получава мястото.</div>
        </>,
        () => onEventSignUp(currentEvent?.id),
        () => {},
        "warning",
        "Да",
        "Не",
      );
      return;
    }

    onEventSignUp(currentEvent?.id);
  };

  // Guests always go straight to login. Prepayment / form-completion
  // requirements are surfaced after auth so we don't gate the login
  // redirect on event configuration.
  const proceedWithGuestReservation = () => {
    if (currentEvent?.id && selectedStudio?.id && currentEvent.date) {
      savePendingReservationIntent({
        eventId: currentEvent.id,
        eventName: currentEvent.name || "",
        eventDate: new Date(currentEvent.date).toISOString(),
        startTime: currentEvent.startTime || "",
        endTime: currentEvent.endTime || "",
        studioId: selectedStudio.id,
        requirePrepayment: !!currentEvent.requirePrepayment,
        isMultisportAvailable: !!currentEvent.isMultisportAvailable,
      });
    }
    navigate(routePaths.login);
  };

  const contentProps: IEventDetailsContentProps = {
    eventName: currentEvent?.name,
    instructor: currentEvent?.instructor,
    dateLabel: dateUtility.formatDate(currentEvent?.date),
    studioName: selectedStudio?.name,
    timeLabel: getEventCalendarTimeLabel({
      startTime: currentEvent?.startTime,
      endTime: currentEvent?.endTime,
      durationInMinutes: currentEvent?.durationInMinutes,
    }),
    address: selectedStudio?.address,
    mapsLink:
      generateMapsLink(selectedStudio?.address, selectedStudio?.addressMapUrl) ??
      "",
    phoneNumber: selectedStudio?.phoneNumber,
    phoneNumberDescription: selectedStudio?.phoneNumberDescription,
    secondaryPhoneNumber: selectedStudio?.secondaryPhoneNumber,
    secondaryPhoneNumberDescription:
      selectedStudio?.secondaryPhoneNumberDescription,
    description: currentEvent?.description,
    reservations: currentEvent?.userReservations,
    warningMessage: onlineReservationsAllowed ? warningMessage : undefined,
    hallName: currentEvent?.hallName,
    price: currentEvent?.price,
    isPassed: currentEvent?.isPassed,
    isEventCancelled: isCancelled,
    isMultisportAvailable: currentEvent?.isMultisportAvailable,
    isRemote: currentEvent?.isRemote,
    meetingUrl: currentEvent?.meetingUrl,
  };

  return {
    contentProps,
    canMakeReservation,
    makeReservationButtonLabel,
    onMakeReservationClick: isGuest
      ? proceedWithGuestReservation
      : handleEventSignUp,
    showManageReservations: hasReservations && !canMakeReservation,
    onManageReservationsClick,
    showNotTakingPlace: isCancelled && !hasReservations,
    showContactStudio: !canMakeReservation && !hasReservations && !isCancelled,
    showContactUsNote: !onlineReservationsAllowed,
    onContactStudioClick: () => callPhoneNumber(selectedStudio?.phoneNumber),
  };
};
